import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AppIcon from '../common/AppIcon';
import { Guest } from '../../services/guests';
import { GuestCategory } from '../../services/guestCategories';

interface GuestBreakdownProps {
  open: boolean;
  guests: Guest[];
  categories: GuestCategory[];
  onClose: () => void;
}

type CountType = 'invited' | 'expected';
type GroupMode = 'category' | 'subcategory' | 'both';

const DEFAULT_COLORS = ['#B07A8F', '#C9A96E', '#6B9E78', '#6A8EB0', '#D4A84E', '#9B8EC4', '#D47E6A', '#7EBCB1'];
const UNCATEGORIZED_COLOR = '#C4BCAC';

// Lighten/darken a hex color by a factor (1 = original, <1 = darker, >1 = lighter)
const shadeColor = (hex: string, factor: number): string => {
  const c = hex.replace('#', '');
  const r = Math.min(255, Math.round(parseInt(c.substring(0, 2), 16) * factor));
  const g = Math.min(255, Math.round(parseInt(c.substring(2, 4), 16) * factor));
  const b = Math.min(255, Math.round(parseInt(c.substring(4, 6), 16) * factor));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

interface SliceItem {
  name: string;
  count: number;
  color: string;
  colorFrom?: string; // gradient start (inner/center), color is the end (outer/edge)
}

// SVG Pie Chart
const PIE_SIZE = 180;
const PIE_RADIUS = 72;
const PIE_CENTER = PIE_SIZE / 2;

const describeArc = (cx: number, cy: number, r: number, startAngle: number, endAngle: number): string => {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y} Z`;
};

const polarToCartesian = (cx: number, cy: number, r: number, angleDeg: number) => {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
};

const PieChart: React.FC<{ items: SliceItem[]; total: number }> = ({ items, total }) => {
  const [hovered, setHovered] = useState<string | null>(null);

  if (total === 0) return null;

  let currentAngle = 0;
  const slices = items.map((item) => {
    const angle = (item.count / total) * 360;
    const startAngle = currentAngle;
    currentAngle += angle;
    const midAngle = startAngle + angle / 2;
    const pct = Math.round((item.count / total) * 100);
    return { ...item, startAngle, endAngle: currentAngle, midAngle, pct };
  });

  const labelR = PIE_RADIUS * 0.62;
  const hoveredSlice = hovered ? slices.find((s) => s.name === hovered) : null;

  // Build gradient defs for slices that have colorFrom
  const gradientSlices = slices.filter((s) => s.colorFrom);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', position: 'relative' }}>
      <svg width={PIE_SIZE} height={PIE_SIZE} viewBox={`0 0 ${PIE_SIZE} ${PIE_SIZE}`}>
        {gradientSlices.length > 0 && (
          <defs>
            {gradientSlices.map((s, i) => {
              // Linear gradient oriented along the slice's mid-angle: category color at center → sub-category at edge
              const edgePoint = polarToCartesian(PIE_CENTER, PIE_CENTER, PIE_RADIUS, s.midAngle);
              return (
                <linearGradient
                  key={`grad-${i}`}
                  id={`slice-grad-${i}`}
                  gradientUnits="userSpaceOnUse"
                  x1={PIE_CENTER}
                  y1={PIE_CENTER}
                  x2={edgePoint.x}
                  y2={edgePoint.y}
                >
                  <stop offset="0%" stopColor={s.colorFrom} />
                  <stop offset="100%" stopColor={s.color} />
                </linearGradient>
              );
            })}
          </defs>
        )}
        {slices.map((s) => {
          const path = s.endAngle - s.startAngle >= 359.99
            ? describeArc(PIE_CENTER, PIE_CENTER, PIE_RADIUS, 0, 359.99)
            : describeArc(PIE_CENTER, PIE_CENTER, PIE_RADIUS, s.startAngle, s.endAngle);
          const gradIdx = s.colorFrom ? gradientSlices.indexOf(s) : -1;
          const fill = gradIdx >= 0 ? `url(#slice-grad-${gradIdx})` : s.color;
          return (
            <path
              key={s.name}
              d={path}
              fill={fill}
              stroke="#fff"
              strokeWidth={1.5}
              style={{ transition: 'opacity 0.15s', opacity: hovered && hovered !== s.name ? 0.5 : 1, cursor: 'pointer' }}
              onMouseEnter={() => setHovered(s.name)}
              onMouseLeave={() => setHovered(null)}
            />
          );
        })}
        {slices.map((s) => {
          if (s.pct < 4) return null;
          const pos = polarToCartesian(PIE_CENTER, PIE_CENTER, labelR, s.midAngle);
          return (
            <text
              key={`lbl-${s.name}`}
              x={pos.x}
              y={pos.y}
              textAnchor="middle"
              dominantBaseline="central"
              fill="#fff"
              fontSize={11}
              fontWeight={600}
              style={{ pointerEvents: 'none' }}
            >
              {s.pct}%
            </text>
          );
        })}
      </svg>
      {hoveredSlice && (
        <Box
          sx={{
            position: 'absolute',
            bottom: -24,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'grey.800',
            color: '#fff',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            fontSize: 12,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}
        >
          {hoveredSlice.name} — {hoveredSlice.count} ({hoveredSlice.pct}%)
        </Box>
      )}
    </Box>
  );
};

// Animated height wrapper
const AnimatedContent: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const innerRef = useRef<HTMLDivElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!innerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setHeight(entry.contentRect.height);
    });
    observer.observe(innerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={outerRef}
      sx={{
        overflow: 'hidden',
        transition: 'height 0.25s ease',
        height: height !== undefined ? height : 'auto',
      }}
    >
      <div ref={innerRef}>{children}</div>
    </Box>
  );
};

const GuestBreakdown: React.FC<GuestBreakdownProps> = ({ open, guests, categories, onClose }) => {
  const [countType, setCountType] = useState<CountType>('invited');
  const [groupMode, setGroupMode] = useState<GroupMode>('category');

  const catMap = Object.fromEntries(categories.map((c) => [c.id, c]));

  const getCount = (guest: Guest) =>
    countType === 'invited' ? guest.num_invited : guest.num_expected_to_attend;

  const total = useMemo(() => guests.reduce((sum, g) => sum + getCount(g), 0), [guests, countType]);

  const breakdown = useMemo(() => {
    if (groupMode === 'category' || groupMode === 'subcategory') {
      const field = groupMode === 'category' ? 'category_level1_id' : 'category_level2_id';
      const groups: Record<string, { name: string; color: string | null; count: number }> = {};
      let uncategorized = 0;
      for (const g of guests) {
        const catId = g[field];
        if (catId && catMap[catId]) {
          if (!groups[catId]) groups[catId] = { name: catMap[catId].name, color: catMap[catId].color, count: 0 };
          groups[catId].count += getCount(g);
        } else {
          uncategorized += getCount(g);
        }
      }
      return { type: 'flat' as const, label: groupMode === 'category' ? 'Category' : 'Sub Category', groups: Object.values(groups), uncategorized };
    }

    // Both
    const catGroups: Record<string, {
      name: string;
      color: string | null;
      total: number;
      subs: Record<string, { name: string; color: string | null; count: number }>;
      uncategorizedSub: number;
    }> = {};
    let uncategorized = 0;

    for (const g of guests) {
      const cat1Id = g.category_level1_id;
      const cat2Id = g.category_level2_id;
      const count = getCount(g);

      if (!cat1Id || !catMap[cat1Id]) {
        uncategorized += count;
        continue;
      }

      if (!catGroups[cat1Id]) {
        catGroups[cat1Id] = { name: catMap[cat1Id].name, color: catMap[cat1Id].color, total: 0, subs: {}, uncategorizedSub: 0 };
      }
      catGroups[cat1Id].total += count;

      if (cat2Id && catMap[cat2Id]) {
        if (!catGroups[cat1Id].subs[cat2Id]) {
          catGroups[cat1Id].subs[cat2Id] = { name: catMap[cat2Id].name, color: catMap[cat2Id].color, count: 0 };
        }
        catGroups[cat1Id].subs[cat2Id].count += count;
      } else {
        catGroups[cat1Id].uncategorizedSub += count;
      }
    }

    return { type: 'both' as const, catGroups: Object.values(catGroups), uncategorized };
  }, [guests, countType, groupMode, catMap]);

  const pieItems = useMemo((): SliceItem[] => {
    if (breakdown.type === 'flat') {
      const items: SliceItem[] = breakdown.groups.map((g, i) => ({
        name: g.name,
        count: g.count,
        color: g.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length],
      }));
      if (breakdown.uncategorized > 0) {
        items.push({ name: 'Uncategorized', count: breakdown.uncategorized, color: UNCATEGORIZED_COLOR });
      }
      return items;
    }
    // "Both" mode — show individual sub-categories as slices with gradient from category → sub-category color
    const items: SliceItem[] = [];
    breakdown.catGroups.forEach((cat, i) => {
      const baseColor = cat.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length];
      const subs = Object.values(cat.subs);
      if (subs.length === 0) {
        items.push({ name: cat.name, count: cat.total, color: baseColor });
      } else {
        subs.forEach((sub, si) => {
          const lightness = 1 - (si / (subs.length + 1)) * 0.45;
          const subColor = sub.color || shadeColor(baseColor, lightness);
          items.push({ name: `${cat.name} — ${sub.name}`, count: sub.count, color: subColor, colorFrom: baseColor });
        });
        if (cat.uncategorizedSub > 0) {
          const shaded = shadeColor(baseColor, 0.55);
          items.push({ name: `${cat.name} — Uncategorized`, count: cat.uncategorizedSub, color: shaded, colorFrom: baseColor });
        }
      }
    });
    if (breakdown.uncategorized > 0) {
      items.push({ name: 'Uncategorized', count: breakdown.uncategorized, color: UNCATEGORIZED_COLOR });
    }
    return items;
  }, [breakdown]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: { transition: 'height 0.25s ease' },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        Guest Count Breakdown
        <IconButton onClick={onClose} size="small" sx={{ color: 'grey.500' }}>
          <AppIcon name="close" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 3 }}>
        <AnimatedContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <ToggleButtonGroup
                value={countType}
                exclusive
                onChange={(_, v) => v && setCountType(v)}
                size="small"
              >
                <ToggleButton value="invited">Invited</ToggleButton>
                <ToggleButton value="expected">Expected</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup
                value={groupMode}
                exclusive
                onChange={(_, v) => v && setGroupMode(v)}
                size="small"
              >
                <ToggleButton value="category">Category</ToggleButton>
                <ToggleButton value="subcategory">Sub Category</ToggleButton>
                <ToggleButton value="both">Both</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, alignItems: { sm: 'flex-start' } }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {breakdown.type === 'flat' && (
                  <TableContainer sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 600 }}>{breakdown.label}</TableCell>
                          <TableCell sx={{ fontWeight: 600, width: 80 }} align="right">Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {breakdown.groups.map((g, i) => (
                          <TableRow key={g.name}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: g.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length], flexShrink: 0 }} />
                                {g.name}
                              </Box>
                            </TableCell>
                            <TableCell align="right">{g.count}</TableCell>
                          </TableRow>
                        ))}
                        {breakdown.uncategorized > 0 && (
                          <TableRow>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: UNCATEGORIZED_COLOR, flexShrink: 0 }} />
                                <span style={{ fontStyle: 'italic', color: '#999' }}>Uncategorized</span>
                              </Box>
                            </TableCell>
                            <TableCell align="right">{breakdown.uncategorized}</TableCell>
                          </TableRow>
                        )}
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{total}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {breakdown.type === 'both' && (
                  <TableContainer sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Category / Sub Category</TableCell>
                          <TableCell sx={{ fontWeight: 600, width: 80 }} align="right">Count</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {breakdown.catGroups.map((cat, i) => (
                          <React.Fragment key={cat.name}>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                              <TableCell sx={{ fontWeight: 600 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: cat.color || DEFAULT_COLORS[i % DEFAULT_COLORS.length], flexShrink: 0 }} />
                                  {cat.name}
                                </Box>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>{cat.total}</TableCell>
                            </TableRow>
                            {Object.values(cat.subs).map((sub) => (
                              <TableRow key={sub.name}>
                                <TableCell sx={{ pl: 4 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {sub.color && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: sub.color, flexShrink: 0 }} />}
                                    {sub.name}
                                  </Box>
                                </TableCell>
                                <TableCell align="right">{sub.count}</TableCell>
                              </TableRow>
                            ))}
                            {cat.uncategorizedSub > 0 && (
                              <TableRow>
                                <TableCell sx={{ pl: 4, fontStyle: 'italic', color: 'text.secondary' }}>Uncategorized</TableCell>
                                <TableCell align="right">{cat.uncategorizedSub}</TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                        {breakdown.uncategorized > 0 && (
                          <TableRow>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: UNCATEGORIZED_COLOR, flexShrink: 0 }} />
                                <span style={{ fontStyle: 'italic', color: '#999' }}>Uncategorized</span>
                              </Box>
                            </TableCell>
                            <TableCell align="right">{breakdown.uncategorized}</TableCell>
                          </TableRow>
                        )}
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell sx={{ fontWeight: 700 }}>Grand Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{total}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

              <Box sx={{ flexShrink: 0, alignSelf: 'center' }}>
                <PieChart items={pieItems} total={total} />
              </Box>
            </Box>
          </Box>
        </AnimatedContent>
      </DialogContent>
    </Dialog>
  );
};

export default GuestBreakdown;
